import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { AccountData, OrderData, PassOut, WaitingListItemData } from '../api';
import { useManagePassesSession } from './hooks/useManagePassesSession';
import { usePassDashboardData } from './hooks/usePassDashboardData';
import { useCustomerWaitlistData } from './hooks/useCustomerWaitlistData';
import { PassService } from '../api/services/PassService';
import { WaitingListService } from '../api/services/WaitingListService';
import { AccountService } from '../api/services/AccountService';

type RegisterToWaitlistParams = {
  orderPageId: string;
  experienceId: string;
  eventId?: string;
  date: string;
  timeSeconds: number;
};

type ManagePassesContextType = {
  passes: PassOut[];
  orders: OrderData[];
  account: AccountData | undefined;
  employeeIdToName: Record<string, string>;
  email: string | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  // Returns false when the response carries nothing to build a session from -- see login() below.
  login: (passes: PassOut[], orders: OrderData[], account: AccountData) => boolean;
  logout: () => void;
  cancelOrder: (orderId: string) => Promise<void>;
  addOrder: (order: OrderData) => Promise<void>;
  // Returns the revalidation promise so callers that navigate straight to freshly created data
  // (buy-again lands on the new pass) can wait for the lookup to catch up first.
  mutate: () => Promise<unknown>;
  waitlistItems: WaitingListItemData[];
  registerToWaitlist: (params: RegisterToWaitlistParams) => Promise<void>;
  cancelWaitlistItem: (waitingListItemId: string) => Promise<void>;
};

const ManagePassesContext = createContext<ManagePassesContextType | undefined>(undefined);

export const useManagePasses = () => {
  const ctx = useContext(ManagePassesContext);
  if (!ctx) throw new Error('useManagePasses must be used within ManagePassesProvider');
  return ctx;
};

type ManagePassesProviderProps = {
  children: React.ReactNode;
  preloadedAccountId?: string;
};

export const ManagePassesProvider = ({ children, preloadedAccountId }: ManagePassesProviderProps) => {
  const navigate = useNavigate();
  const { getSession, saveSession, clearSession } = useManagePassesSession();

  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [sessionOrderNumber, setSessionOrderNumber] = useState<number | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const {
    passes,
    orders,
    account: dashboardAccount,
    employeeIdToName,
    isLoading,
    mutate,
  } = usePassDashboardData(sessionOrderNumber, sessionEmail);

  const { data: preloadedAccount } = useSWR<AccountData>(
    !isLoggedIn && preloadedAccountId ? ['preloaded-account', preloadedAccountId] : null,
    ([, id]: [string, string]) => AccountService.getAccount(id),
  );

  const account = dashboardAccount ?? preloadedAccount;

  const { waitlistItems, mutate: mutateWaitlist } = useCustomerWaitlistData(sessionEmail, account?.id || null);

  useEffect(() => {
    const session = getSession();
    // A session missing either half cannot key the dashboard lookup, so restoring it would leave
    // the portal stuck with no data. Drop it and send the customer back to the login form.
    if (session?.email && session.orderNumber) {
      setSessionEmail(session.email);
      setSessionOrderNumber(session.orderNumber);
      setIsLoggedIn(true);
    } else if (session) {
      clearSession();
    }
  }, []);

  // The dashboard lookup is keyed on email + pass order number, so both must be derivable. Without
  // them the portal would sign the customer in and then render nothing at all, so refuse instead
  // and let the caller decide what to show.
  const login = (loginPasses: PassOut[], loginOrders: OrderData[], loginAccount: AccountData): boolean => {
    const email = loginPasses[0]?.email || loginOrders[0]?.email || '';
    const orderNum = loginPasses[0]?.order_number || 0;
    if (!email || !orderNum) {
      return false;
    }
    setSessionEmail(email);
    setSessionOrderNumber(orderNum);
    setIsLoggedIn(true);
    saveSession({ email, orderNumber: orderNum, accountId: loginAccount.id });
    navigate('/passes/dashboard', { replace: true });
    return true;
  };

  const logout = () => {
    clearSession();
    setSessionEmail(null);
    setSessionOrderNumber(null);
    setIsLoggedIn(false);
    navigate('/passes', { replace: true });
  };

  const cancelOrder = async (orderId: string) => {
    if (!sessionEmail) return;
    await PassService.customerCancelOrder({ order_id: orderId, email: sessionEmail });
    await mutate();
  };

  const registerToWaitlist = async ({ orderPageId, experienceId, eventId, date }: RegisterToWaitlistParams) => {
    if (!sessionEmail || !account || passes.length === 0) {
      return;
    }
    const pass = passes[0];
    await WaitingListService.registerToWaitingList({
      language: pass.language || 'en',
      account_id: account.id,
      experience_id: experienceId,
      order_page_id: orderPageId,
      personal_details: {
        firstname: pass.firstname,
        lastname: pass.lastname,
        email: pass.email,
        phone: pass.phone,
      },
      persons: 1,
      date,
      event_id: eventId,
    });
    await mutateWaitlist();
  };

  const cancelWaitlistItem = async (waitingListItemId: string) => {
    if (!sessionEmail) return;
    await WaitingListService.customerCancelWaitingListItem({
      email: sessionEmail,
      waiting_list_item_id: waitingListItemId,
    });
    await mutateWaitlist();
  };

  const addOrder = async (order: OrderData) => {
    await mutate(
      (current) =>
        current
          ? {
              ...current,
              orders: [...current.orders, order],
            }
          : current,
      { revalidate: true },
    );
  };

  return (
    <ManagePassesContext.Provider
      value={{
        passes,
        orders,
        account,
        employeeIdToName,
        email: sessionEmail,
        isLoading,
        isLoggedIn,
        login,
        logout,
        cancelOrder,
        addOrder,
        mutate: () => mutate(),
        waitlistItems,
        registerToWaitlist,
        cancelWaitlistItem,
      }}>
      {children}
    </ManagePassesContext.Provider>
  );
};
